import { BrowserRouter as Router, Routes, Route, useParams, useNavigate } from 'react-router-dom';
import { lazy, Suspense, useEffect } from 'react';
import { Navigation } from './components/Navigation';
import ConsentBanner from './components/ConsentBanner';
import { HomePage } from './components/HomePage';
import { LanguageProvider } from './contexts/LanguageContext';
import { useLanguage } from '@/hooks/useLanguage';
import CriticalCSS, { criticalStyles } from './components/CriticalCSS';
import PreloadCritical from './components/PreloadCritical';
import FontOptimizer from './components/FontOptimizer';
// import ServiceWorkerManager from './components/ServiceWorkerManager';
import CriticalResourceHints from './components/CriticalResourceHints';
import PerformanceMonitor from './components/PerformanceMonitor';

// Lazy load heavy components for better performance
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogPost = lazy(() => import('./components/BlogArticle').then(module => ({ default: module.BlogArticle })));
const PodcastPage = lazy(() => import('./pages/PodcastPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const CookiePage = lazy(() => import('./pages/CookiePage'));

// Lazy load SEO and analytics components (non-critical for initial render)
const SchemaMarkup = lazy(() => import('./components/SchemaMarkup').then(module => ({ default: module.SchemaMarkup })));
const AnalyticsTracker = lazy(() => import('./components/AnalyticsTracker').then(module => ({ default: module.AnalyticsTracker })));
const PerformanceOptimizer = lazy(() => import('./components/PerformanceOptimizer').then(module => ({ default: module.PerformanceOptimizer })));
const SEOOptimizer = lazy(() => import('./components/SEOOptimizer').then(module => ({ default: module.SEOOptimizer })));
const LocalSEOPanama = lazy(() => import('./components/LocalSEOPanama').then(module => ({ default: module.LocalSEOPanama })));
const UXCROOptimizer = lazy(() => import('./components/UXCROOptimizer').then(module => ({ default: module.UXCROOptimizer })));

// Loading component for better UX
const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
  </div>
);

// Route wrapper for blog posts to pass slug param and sync language from URL
const BlogPostRoute = () => {
  const { slug, lang } = useParams();
  const navigate = useNavigate();
  const { setLanguage } = useLanguage();

  useEffect(() => {
    if (lang && (lang === 'it' || lang === 'es' || lang === 'en')) {
      setLanguage(lang as 'it' | 'es' | 'en');
    }
  }, [lang, setLanguage]);

  return <BlogPost articleId={slug || ''} onBack={() => navigate(-1)} />;
};

// Route wrapper for blog list to sync language from URL when prefixed
const BlogPageRoute = () => {
  const { lang } = useParams();
  const { setLanguage } = useLanguage();

  useEffect(() => {
    if (lang && (lang === 'it' || lang === 'es' || lang === 'en')) {
      setLanguage(lang as 'it' | 'es' | 'en');
    }
  }, [lang, setLanguage]);

  return (
    <Suspense fallback={<LoadingSpinner />}>
      <BlogPage />
    </Suspense>
  );
};

// Route wrapper for home to sync language from URL when prefixed
const HomePageRoute = () => {
  const { lang } = useParams();
  const { setLanguage } = useLanguage();

  useEffect(() => {
    if (lang && (lang === 'it' || lang === 'es' || lang === 'en')) {
      setLanguage(lang as 'it' | 'es' | 'en');
    }
  }, [lang, setLanguage]);

  return <HomePage />;
};

// Route wrapper for podcast to sync language from URL when prefixed
const PodcastPageRoute = () => {
  const { lang } = useParams();
  const { setLanguage } = useLanguage();

  useEffect(() => {
    if (lang && (lang === 'it' || lang === 'es' || lang === 'en')) {
      setLanguage(lang as 'it' | 'es' | 'en');
    }
  }, [lang, setLanguage]);

  return (
    <Suspense fallback={<LoadingSpinner />}>
      <PodcastPage />
    </Suspense>
  );
};

function AppContent() {
  const { language } = useLanguage();
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);
  
  return (
    <Router>
      {/* Critical performance optimizations loaded immediately */}
      <FontOptimizer />
      {/* <ServiceWorkerManager /> */}
      <CriticalResourceHints />
      <PerformanceMonitor />
      
      {/* Preload critical resources immediately */}
      <PreloadCritical />
      
      {/* Inject critical CSS for better performance */}
      <CriticalCSS styles={criticalStyles} />
      
      {/* Critical components loaded immediately */}
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700">
        <Navigation />
        <a href="#main-content" className="sr-only focus:outline-none focus-visible:not-sr-only focus-visible:px-4 focus-visible:py-2 focus-visible:bg-white focus-visible:text-blue-700">Salta al contenuto</a>
        <ConsentBanner />
        <main id="main-content" role="main">
        <Routes>
          <Route path="/" element={<HomePage />} />
          {/* Language-prefixed home (e.g., /en) */}
          <Route path="/:lang" element={<HomePageRoute />} />
          <Route 
            path="/blog" 
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <BlogPage />
              </Suspense>
            } 
          />
          {/* Language-prefixed blog list (e.g., /it/blog) */}
          <Route 
            path="/:lang/blog" 
            element={<BlogPageRoute />} 
          />
          <Route 
            path="/blog/:slug" 
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <BlogPostRoute />
              </Suspense>
            } 
          />
          {/* Language-prefixed blog article (e.g., /it/blog/:slug) */}
          <Route 
            path="/:lang/blog/:slug" 
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <BlogPostRoute />
              </Suspense>
            } 
          />
          {/* Language-suffixed blog article (e.g., /blog/:slug/es) */}
          <Route 
            path="/blog/:slug/:lang" 
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <BlogPostRoute />
              </Suspense>
            } 
          />
          <Route 
            path="/podcast" 
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <PodcastPage />
              </Suspense>
            } 
          />
          {/* Language-prefixed podcast (e.g., /en/podcast) */}
          <Route 
            path="/:lang/podcast" 
            element={<PodcastPageRoute />} 
          />
          {/* Privacy & Cookies */}
          <Route 
            path="/privacy" 
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <PrivacyPage />
              </Suspense>
            } 
          />
          <Route 
            path="/:lang/privacy" 
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <PrivacyPage />
              </Suspense>
            } 
          />
          <Route 
            path="/cookies" 
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <CookiePage />
              </Suspense>
            } 
          />
          <Route 
            path="/:lang/cookies" 
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <CookiePage />
              </Suspense>
            } 
          />
        </Routes>
        </main>
      </div>
      
      {/* Non-critical components loaded after initial render */}
      <Suspense fallback={null}>
        <PerformanceOptimizer 
          enablePreloading={true}
          enableResourceHints={true}
          enableCriticalCSS={true}
        />
      </Suspense>
      
      <Suspense fallback={null}>
        <AnalyticsTracker 
          googleAnalyticsId="G-BKDGNKX0Z1" 
          facebookPixelId="FB_PIXEL_ID" 
        />
      </Suspense>
      
      <Suspense fallback={null}>
        <SEOOptimizer 
          page="home"
          language={language}
        />
      </Suspense>
      
      <Suspense fallback={null}>
        <LocalSEOPanama 
          language={language}
          businessType="legal"
          location="panama-city"
        />
      </Suspense>
      
      <Suspense fallback={null}>
        <UXCROOptimizer 
          language={language}
          enableHeatmapTracking={true}
          enableScrollTracking={true}
          enableFormAnalytics={true}
          enableExitIntent={true}
          enableMobileOptimization={true}
          enableAccessibility={true}
          enableMicroInteractions={true}
          enableABTesting={false}
        />
      </Suspense>
      
      <Suspense fallback={null}>
        <SchemaMarkup 
          type="organization"
          data={{
            name: "RS International Services",
            description: "Consulenza specializzata per residenza e cittadinanza panamense",
            url: "https://residenciapanama.com",
            telephone: "(+507) 6958 4333",
            email: "info@residenciapanama.com"
          }}
        />
      </Suspense>
    </Router>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AppContent />
    </LanguageProvider>
  );
}

export default App;